import { useEffect, useRef, useCallback } from "react";

interface UseActivityDetectionOptions {
  onActivity: () => void;
  events?: string[];
  target?: EventTarget;
}

const DEFAULT_ACTIVITY_EVENTS = ["scroll", "mousemove", "click", "keypress"];

export const useActivityDetection = ({
  onActivity,
  events = DEFAULT_ACTIVITY_EVENTS,
  target = window,
}: UseActivityDetectionOptions) => {
  const lastActivityRef = useRef<number>(Date.now());

  const handleActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    onActivity();
  }, [onActivity]);

  const getTimeSinceLastActivity = useCallback(() => {
    return Date.now() - lastActivityRef.current;
  }, []);

  useEffect(() => {
    const passiveEvents = new Set([
      "scroll",
      "wheel",
      "touchstart",
      "touchmove",
      "mousemove",
    ]);

    events.forEach((eventName) => {
      const options = passiveEvents.has(eventName)
        ? { passive: true }
        : undefined;
      target.addEventListener(eventName, handleActivity, options);
    });

    return () => {
      events.forEach((eventName) => {
        target.removeEventListener(eventName, handleActivity);
      });
    };
  }, [events, target, handleActivity]);

  return { getTimeSinceLastActivity };
};
